import React, {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useState,
} from 'react';
import SideDrawer from '@common/side-drawer/side-drawer';
import { PreferredDealerDrawerContent } from '@views/preferred-dealer-view/hooks/use-preferred-dealer-drawer-content';
import ZoomResetUtil from '@/components/utils/zoom-reset/zoom-reset';
import { VehicleDetail } from '@models/profile-with-vehicles';

export interface SideDrawerProps {
    name: string;
    children?: ReactNode;
    preferredDealerDrawerContent?: PreferredDealerDrawerContent;
    appConfig?: any;
    singleVehicle?: VehicleDetail;
}

export interface SideDrawerContextProps {
    drawerType: SideDrawerProps;
    isVisible?: boolean;
    setDrawerContext?: (updates: SideDrawerContextProps) => void;
}

export const SideDrawerContext = createContext({
    drawerType: { name: '' },
    isVisible: false,
    setDrawerContext: () => null,
} as any);

const SideDrawerProvider = ({ children }) => {
    const [sideDrawerContext, setSideDrawerContext] =
        useState<SideDrawerContextProps | null>({
            drawerType: { name: '' },
            isVisible: false,
        });
    const [isVisible, setIsVisible] = useState<boolean>(false);

    const setDrawerContext = (
        sideDrawerContextUpdates: SideDrawerContextProps
    ) => {
        const zoomResetUtil = new ZoomResetUtil();
        zoomResetUtil.resetZoom();
        const sideDrawerPortal = document.getElementById('side-drawer-portal');
        sideDrawerPortal && sideDrawerPortal.classList.remove('closed');

        setSideDrawerContext((prevState) => {
            return {
                ...prevState,
                ...sideDrawerContextUpdates,
            };
        });

        if (sideDrawerContextUpdates.drawerType?.name?.length) {
            setIsVisible(true);
        } else {
            setIsVisible(false);
        }
    };

    const resetContext = useCallback(() => {
        setSideDrawerContext({
            drawerType: { name: '' },
            isVisible: false,
        });
    }, []);

    const getContext = useCallback(() => {
        return {
            sideDrawerContext,
            isVisible,
            setIsVisible,
            setDrawerContext,
            resetContext,
        };
    }, [sideDrawerContext, isVisible]);

    return (
        <SideDrawerContext.Provider value={getContext()}>
            {children}
            <SideDrawer />
        </SideDrawerContext.Provider>
    );
};

export const useSideDrawerContext = () => useContext(SideDrawerContext);

export default SideDrawerProvider;
